if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.thumbnailsInitialized = false;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.touchStartY = 0;
        this.touchEndY = 0;
        this.isZoomed = false;
        this.isPanning = false;
        this.startX = 0;
        this.startY = 0;
        this.translateX = 0;
        this.translateY = 0;
      }

      hide() {
        super.hide();
        // Remove keyboard listener when modal closes
        if (this.keyboardHandler) {
          document.removeEventListener('keydown', this.keyboardHandler);
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        if (!this.thumbnailsInitialized) {
          this.initThumbnails();
          this.initArrows();
          this.initSwipe();
          this.initKeyboard();
          this.initZoom();
          this.thumbnailsInitialized = true;
        }
      }

      initThumbnails() {
        const thumbnails = this.querySelectorAll('.product-media-modal__thumbnail');
        thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const mediaId = thumbnail.getAttribute('data-media-id');
            this.switchMedia(mediaId);
          });
        });
      }

      initArrows() {
        const prevButton = this.querySelector('.product-media-modal__arrow--prev');
        const nextButton = this.querySelector('.product-media-modal__arrow--next');

        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.navigatePrevious();
            return false;
          }, true);
        }

        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.navigateNext();
            return false;
          }, true);
        }
      }

      initSwipe() {
        const contentArea = this.querySelector('.product-media-modal__content');

        if (contentArea) {
          contentArea.addEventListener('touchstart', (e) => {
            this.touchStartX = e.changedTouches[0].clientX;
            this.touchStartY = e.changedTouches[0].clientY;
          }, { passive: true });

          contentArea.addEventListener('touchmove', (e) => {
            if (!this.touchStartX) return;
            this.touchEndX = e.changedTouches[0].clientX;
            this.touchEndY = e.changedTouches[0].clientY;
          }, { passive: true });

          contentArea.addEventListener('touchend', (e) => {
            if (!this.touchStartX || !this.touchEndX) return;
            this.handleSwipe();
            // Reset values
            this.touchStartX = 0;
            this.touchEndX = 0;
            this.touchStartY = 0;
            this.touchEndY = 0;
          }, { passive: true });
        }
      }

      initKeyboard() {
        // Store the handler so we can remove it later
        this.keyboardHandler = (e) => {
          // Only handle if modal is open
          if (!this.hasAttribute('open')) return;

          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.navigatePrevious();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.navigateNext();
          }
        };

        document.addEventListener('keydown', this.keyboardHandler);
      }

      handleSwipe() {
        const swipeThreshold = 50;
        const diffX = this.touchStartX - this.touchEndX;
        const diffY = this.touchStartY - this.touchEndY;

        // Only trigger if horizontal swipe is stronger than vertical
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > swipeThreshold) {
          if (diffX > 0) {
            // Swipe left - next image
            this.navigateNext();
          } else {
            // Swipe right - previous image
            this.navigatePrevious();
          }
        }
      }

      navigateNext() {
        const allMediaItems = Array.from(this.querySelectorAll('.product__media-item'));
        const currentActive = this.querySelector('.product__media-item.active');
        const currentIndex = allMediaItems.indexOf(currentActive);

        if (currentIndex < allMediaItems.length - 1) {
          const nextMedia = allMediaItems[currentIndex + 1];
          const mediaId = nextMedia.getAttribute('data-media-id');
          this.switchMedia(mediaId);
        }
      }

      navigatePrevious() {
        const allMediaItems = Array.from(this.querySelectorAll('.product__media-item'));
        const currentActive = this.querySelector('.product__media-item.active');
        const currentIndex = allMediaItems.indexOf(currentActive);

        if (currentIndex > 0) {
          const prevMedia = allMediaItems[currentIndex - 1];
          const mediaId = prevMedia.getAttribute('data-media-id');
          this.switchMedia(mediaId);
        }
      }

      switchMedia(mediaId) {
        // Reset zoom and pan state when switching images
        this.isZoomed = false;
        this.translateX = 0;
        this.translateY = 0;
        this.isPanning = false;

        // Remove has-zoom class from content area
        const contentArea = this.querySelector('.product-media-modal__content');
        if (contentArea) {
          contentArea.classList.remove('has-zoom');
        }

        // Remove active class and zoomed class from all media items, and reset transform
        this.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.remove('active', 'zoomed');
          item.style.transform = '';
        });

        // Remove active class from all thumbnails
        this.querySelectorAll('.product-media-modal__thumbnail').forEach((thumb) => {
          thumb.classList.remove('active');
        });

        // Add active class to selected media and thumbnail
        const activeMedia = this.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
        const activeThumbnail = this.querySelector(`.product-media-modal__thumbnail[data-media-id="${mediaId}"]`);

        if (activeMedia) {
          activeMedia.classList.add('active');
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          const activeMediaTemplate = activeMedia.querySelector('template');
          const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

          const container = this.querySelector('.product-media-modal__content');
          if (container) {
            container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
          }

          const deferredMedia = activeMedia.querySelector('deferred-media');
          if (
            deferredMedia &&
            deferredMedia.nodeName == 'DEFERRED-MEDIA' &&
            activeMediaContent &&
            activeMediaContent.querySelector('.js-youtube')
          ) {
            deferredMedia.loadContent();
          }
        }

        if (activeThumbnail) {
          activeThumbnail.classList.add('active');
        }
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        this.switchMedia(mediaId);
      }

      initZoom() {
        const contentArea = this.querySelector('.product-media-modal__content');

        if (contentArea) {
          // Click to zoom in/out
          contentArea.addEventListener('click', (e) => {
            // Only zoom if clicking on the active media item, not on arrows or thumbnails
            const activeMedia = contentArea.querySelector('.product__media-item.active');
            if (activeMedia && (e.target === activeMedia || activeMedia.contains(e.target))) {
              this.toggleZoom(activeMedia);
            }
          });

          // Mouse move for panning based on cursor position
          contentArea.addEventListener('mousemove', (e) => {
            if (!this.isZoomed) return;

            const activeMedia = contentArea.querySelector('.product__media-item.active');
            if (activeMedia && (e.target === activeMedia || activeMedia.contains(e.target))) {
              const rect = activeMedia.getBoundingClientRect();

              // Calculate cursor position relative to the image (0 to 1)
              const x = (e.clientX - rect.left) / rect.width;
              const y = (e.clientY - rect.top) / rect.height;

              // Calculate the maximum pan distance
              // When zoomed at 2.5x, the visible area is 1/2.5 = 40% of the image
              // So we can pan the remaining 60% (30% in each direction from center)
              const scale = 2.5;
              const maxPanPercent = ((scale - 1) / scale) * 50; // 30% for 2.5x zoom

              // Map cursor position (0 to 1) to pan range
              // When cursor is at left (x=0), show left side (panX should be positive)
              // When cursor is at right (x=1), show right side (panX should be negative)
              const panX = (0.5 - x) * maxPanPercent * 2;
              const panY = (0.5 - y) * maxPanPercent * 2;

              // Apply transform with percentage-based translation for smooth, precise movement
              activeMedia.style.transform = `scale(${scale}) translate(${panX}%, ${panY}%)`;
            }
          });

          // Reset position when mouse leaves
          contentArea.addEventListener('mouseleave', () => {
            if (this.isZoomed) {
              const activeMedia = contentArea.querySelector('.product__media-item.active');
              if (activeMedia) {
                activeMedia.style.transform = 'scale(2.5) translate(0%, 0%)';
              }
            }
          });

          // Touch events for mobile - use drag-based panning
          let touchStartX = 0;
          let touchStartY = 0;

          contentArea.addEventListener('touchstart', (e) => {
            if (!this.isZoomed) return;

            const activeMedia = contentArea.querySelector('.product__media-item.active');
            if (activeMedia && (e.target === activeMedia || activeMedia.contains(e.target))) {
              this.isPanning = true;
              touchStartX = e.touches[0].clientX - this.translateX;
              touchStartY = e.touches[0].clientY - this.translateY;
            }
          }, { passive: false });

          contentArea.addEventListener('touchmove', (e) => {
            if (!this.isPanning || !this.isZoomed) return;

            const activeMedia = contentArea.querySelector('.product__media-item.active');
            if (activeMedia) {
              e.preventDefault();
              this.translateX = e.touches[0].clientX - touchStartX;
              this.translateY = e.touches[0].clientY - touchStartY;
              activeMedia.style.transform = `scale(2.5) translate(${this.translateX / 2.5}px, ${this.translateY / 2.5}px)`;
            }
          }, { passive: false });

          contentArea.addEventListener('touchend', () => {
            this.isPanning = false;
          });
        }
      }

      toggleZoom(mediaItem) {
        if (!mediaItem) return;

        this.isZoomed = !this.isZoomed;
        const contentArea = this.querySelector('.product-media-modal__content');

        if (this.isZoomed) {
          // Reset pan position when zooming in
          this.translateX = 0;
          this.translateY = 0;
          mediaItem.classList.add('zoomed');
          mediaItem.style.transform = 'scale(2.5) translate(0%, 0%)';
          if (contentArea) {
            contentArea.classList.add('has-zoom');
          }
        } else {
          // Reset pan position when zooming out
          this.translateX = 0;
          this.translateY = 0;
          mediaItem.classList.remove('zoomed');
          mediaItem.style.transform = '';
          if (contentArea) {
            contentArea.classList.remove('has-zoom');
          }
        }
      }
    }
  );
}
